import { ONGOING_PROJECT_VALIDATION_LIMITS } from "../constants/ongoingProjectConstants";

// Validate required field
export const validateRequired = (value, fieldName = "Field") => {
  if (!value || (typeof value === "string" && !value.trim())) {
    return `${fieldName} is required`;
  }
  return null;
};

// Validate project name
export const validateProjectName = (projectName) => {
  if (!projectName || !projectName.trim()) {
    return "Project name is required";
  }

  const trimmedName = projectName.trim();

  if (
    trimmedName.length <
    ONGOING_PROJECT_VALIDATION_LIMITS.PROJECT_NAME_MIN_LENGTH
  ) {
    return `Project name must be at least ${ONGOING_PROJECT_VALIDATION_LIMITS.PROJECT_NAME_MIN_LENGTH} characters`;
  }

  if (
    trimmedName.length >
    ONGOING_PROJECT_VALIDATION_LIMITS.PROJECT_NAME_MAX_LENGTH
  ) {
    return `Project name cannot exceed ${ONGOING_PROJECT_VALIDATION_LIMITS.PROJECT_NAME_MAX_LENGTH} characters`;
  }

  return null;
};

// Validate amount
export const validateAmount = (amount, fieldName = "Amount") => {
  if (amount === "" || amount === null || amount === undefined) {
    return null; // Allow empty amounts (will be treated as 0)
  }

  const numAmount = parseFloat(amount);

  if (isNaN(numAmount)) {
    return `${fieldName} must be a valid number`;
  }

  if (numAmount < ONGOING_PROJECT_VALIDATION_LIMITS.AMOUNT_MIN) {
    return `${fieldName} cannot be negative`;
  }

  if (numAmount > ONGOING_PROJECT_VALIDATION_LIMITS.AMOUNT_MAX) {
    return `${fieldName} cannot exceed ₹99,99,99 lakhs`;
  }

  return null;
};

// Validate year of commencement
export const validateYear = (year) => {
  if (!year) {
    return "Year of commencement is required";
  }

  const numYear = parseInt(year);

  if (isNaN(numYear)) {
    return "Year must be a valid number";
  }

  if (
    numYear < ONGOING_PROJECT_VALIDATION_LIMITS.YEAR_MIN ||
    numYear > ONGOING_PROJECT_VALIDATION_LIMITS.YEAR_MAX
  ) {
    return `Year must be between ${ONGOING_PROJECT_VALIDATION_LIMITS.YEAR_MIN} and ${ONGOING_PROJECT_VALIDATION_LIMITS.YEAR_MAX}`;
  }

  return null;
};

// NEW: Validate remarks
export const validateRemarks = (remarks) => {
  if (!remarks) {
    return null; // Remarks are optional
  }

  const trimmedRemarks = remarks.trim();

  if (
    trimmedRemarks.length > ONGOING_PROJECT_VALIDATION_LIMITS.REMARKS_MAX_LENGTH
  ) {
    return `Remarks cannot exceed ${ONGOING_PROJECT_VALIDATION_LIMITS.REMARKS_MAX_LENGTH} characters`;
  }

  return null;
};

// Validate complete ongoing project
export const validateOngoingProject = (projectData) => {
  const errors = {};

  // Department validation
  const departmentError = validateRequired(
    projectData.department,
    "Department",
  );
  if (departmentError) errors.department = departmentError;

  // Project name validation
  const projectNameError = validateProjectName(projectData.projectName);
  if (projectNameError) errors.projectName = projectNameError;

  // Year validation
  const yearError = validateYear(projectData.yearOfCommencement);
  if (yearError) errors.yearOfCommencement = yearError;

  // Amount validations
  const estimatedCostError = validateAmount(
    projectData.totalEstimatedCost,
    "Total estimated cost",
  );
  if (estimatedCostError) errors.totalEstimatedCost = estimatedCostError;

  const expenditureError = validateAmount(
    projectData.totalExpenditure,
    "Total Clearance",
  );
  if (expenditureError) errors.totalExpenditure = expenditureError;

  // NEW: Remarks validation
  const remarksError = validateRemarks(projectData.remarks);
  if (remarksError) errors.remarks = remarksError;

  // Business logic: expenditure cannot exceed estimated cost
  const estimatedCost = parseFloat(projectData.totalEstimatedCost) || 0;
  const expenditure = parseFloat(projectData.totalExpenditure) || 0;

  if (expenditure > estimatedCost) {
    errors.expenditureExceedsEstimate =
      "Total Clearance cannot exceed total estimated cost";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Sanitize ongoing project data
export const sanitizeOngoingProject = (projectData) => {
  const sanitized = { ...projectData };

  // Trim string fields
  if (sanitized.projectName)
    sanitized.projectName = sanitized.projectName.trim();
  if (sanitized.department) sanitized.department = sanitized.department.trim();
  if (sanitized.yearOfCommencement)
    sanitized.yearOfCommencement = sanitized.yearOfCommencement
      .toString()
      .trim();
  if (sanitized.remarks) sanitized.remarks = sanitized.remarks.trim(); // NEW

  // Convert amounts to numbers
  if (
    sanitized.totalEstimatedCost === "" ||
    sanitized.totalEstimatedCost === null ||
    sanitized.totalEstimatedCost === undefined
  ) {
    sanitized.totalEstimatedCost = 0;
  } else {
    sanitized.totalEstimatedCost =
      parseFloat(sanitized.totalEstimatedCost) || 0;
  }

  if (
    sanitized.totalExpenditure === "" ||
    sanitized.totalExpenditure === null ||
    sanitized.totalExpenditure === undefined
  ) {
    sanitized.totalExpenditure = 0;
  } else {
    sanitized.totalExpenditure = parseFloat(sanitized.totalExpenditure) || 0;
  }

  // Calculate balance remaining
  sanitized.balanceRemaining = Math.max(
    0,
    sanitized.totalEstimatedCost - sanitized.totalExpenditure,
  );

  // Convert year to number
  if (sanitized.yearOfCommencement) {
    sanitized.yearOfCommencement = parseInt(sanitized.yearOfCommencement) || "";
  }

  return sanitized;
};

export default {
  validateRequired,
  validateProjectName,
  validateAmount,
  validateYear,
  validateRemarks, // NEW
  validateOngoingProject,
  sanitizeOngoingProject,
};
